import { LocalDate } from "@js-joda/core";
import { PatientContractDiagnosisCode } from "../../messages/PatientContractDiagnosisCode";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { fmap } from "../../utils/fmap";
import { isDefined } from "../Generic/IsDefined";

export function parseErrorResponse(error: any) {
  if (error === undefined || !Number.isInteger(error.statusCode)) {
    return undefined;
  }

  const parsedBody = fmap(error.body, (x) => JSON.parse(x.toString()));

  switch (error.statusCode) {
    case 403:
      return translate(STRINGS.YOU_ARE_NOT_ALLOWED_TO_EDIT_THIS_FIELD);
    case 409:
      if (parsedBody !== null && parsedBody.resource !== undefined) {
        switch (parsedBody.resource) {
          case "ICD_CODE":
            return translate(STRINGS.YOU_CANT_USE_THE_SAME_ICD_CODE_MORE_THAN_ONCE);
          case "TYPE":
            return translate(STRINGS.YOU_CANT_HAVE_MORE_THAN_ONE_PRINCIPAL_OR_SURGICAL_DIAGNOSES);
          default:
            return undefined;
        }
      }
      return undefined;
    default:
      return undefined;
  }
}

interface DiagnosesMap {
  principal: PatientContractDiagnosisCode | null;
  surgical: PatientContractDiagnosisCode | null;
  others: PatientContractDiagnosisCode[];
}

export function groupDiagnoses(diagnoses: PatientContractDiagnosisCode[]) {
  const diagnosesMap: DiagnosesMap = {
    principal: null,
    surgical: null,
    others: [],
  };

  for (const diagnosis of diagnoses) {
    if (diagnosis.type === "PRINCIPAL") {
      diagnosesMap.principal = diagnosis;
    } else if (diagnosis.type === "SURGICAL") {
      diagnosesMap.surgical = diagnosis;
    } else {
      diagnosesMap.others.push(diagnosis);
    }
  }

  return diagnosesMap;
}

function areSameDates(dateA: LocalDate | null, dateB: LocalDate | null) {
  if (!isDefined(dateA) && !isDefined(dateB)) {
    return true;
  }

  if (!isDefined(dateA) || !isDefined(dateB)) {
    return false;
  }

  return dateA.compareTo(dateB) === 0;
}

export function isEqualCodes(
  codeA: PatientContractDiagnosisCode,
  codeB: PatientContractDiagnosisCode
) {
  return (
    codeA.id === codeB.id &&
    codeA.type === codeB.type &&
    areSameDates(codeA.startDate, codeB.startDate) &&
    areSameDates(codeA.endDate, codeB.endDate)
  );
}

export function findEqualCode(
  codeToFind: PatientContractDiagnosisCode,
  codes: PatientContractDiagnosisCode[]
) {
  for (const icdCode of codes) {
    if (isEqualCodes(codeToFind, icdCode)) {
      return icdCode;
    }
  }

  return null;
}

export function isEqualCodesLists(
  codesListA: PatientContractDiagnosisCode[] = [],
  codesListB: PatientContractDiagnosisCode[] = []
) {
  const areAllCodesEqual = (
    listA: PatientContractDiagnosisCode[] = [],
    listB: PatientContractDiagnosisCode[] = []
  ) => {
    for (const icdCode of listA) {
      const sameCode = findEqualCode(icdCode, listB);
      if (!isDefined(sameCode)) {
        return false;
      }
    }

    return true;
  };

  return areAllCodesEqual(codesListA, codesListB) && areAllCodesEqual(codesListB, codesListA);
}
